import {
    type SortDirection,
    type SortState,
    toMuiSortOrder,
    useTableSort,
} from '@/components/tables/tableSort';
import { useStickyTableChrome } from '@/hooks/useStickyTableChrome';
import { Utils } from '@/types/utils';
import { NullableFormattedDatetime } from '@/utils/datetimes/formatting';
import { removeUndefinedFromArray, uniqueByEquality } from '@/utils/iteration';
import { useOpenLinkWithNewTabSupport } from '@/utils/links';
import { Component, ReturnComponent, Style, genericMemo } from '@/utils/reactTypes';
import { Check, Close } from '@mui/icons-material';
import {
    Box,
    Checkbox,
    CircularProgress,
    SxProps,
    TableCell,
    TableFooter,
    TableRow,
    Theme,
} from '@mui/material';
import MuiTablePagination from '@mui/material/TablePagination';
import _ from 'lodash';
import moment from 'moment';
import { Moment, isMoment } from 'moment-timezone';
import MUIDataTable, {
    MUIDataTableCheckboxProps,
    MUIDataTableColumn,
    MUIDataTableColumnOptions,
    MUIDataTableOptions,
    MUIDataTableProps,
} from 'mui-datatables';
import { ElementType, FC, useEffect, useMemo, useState } from 'react';
import invariant from 'tiny-invariant';

import { pinnedRightCellSx, pinnedRightHeaderSx } from './pinnedColumn';

const isFieldEmpty = (value: unknown) => value === undefined || value === null || value === '';

export const AXLE_TABLE_NONE_TEXT = '—';

// MUI DataTables keeps its own copy of the rows and only refreshes it in
// `componentDidUpdate`, which runs after render. So for one render after `data` changes it
// asks us about rows from the previous set, by an index that can be past the end of the new
// array.
const rowAtDataIndex = <T,>(data: T[], dataIndex: number): T | undefined => data.at(dataIndex);

// <MUIDataTable/> owns its horizontal scroll container, so it is reached by its
// hashed class rather than a ref.
const MUI_DATATABLES_SCROLL_CONTAINER = '[class*="responsiveBase"]';

// Merge sticky-right sx into whatever cell props a pinned column already sets.
const withPinnedSx = (prev: object | undefined, pinnedSx: SxProps<Theme>) => {
    const prevSx = (prev as { sx?: SxProps<Theme> } | undefined)?.sx;
    return { ...prev, sx: [prevSx, pinnedSx].flat().filter(Boolean) };
};

export type RawColumn<T = unknown> = {
    label?: string;
    field?: string;
    hidden?: boolean;
    hiddenWithOptionToShow?: boolean;
    mobile?: boolean;
    rowToComponent?: (row: T) => ReturnComponent;
    boolean?: boolean;
    booleanLabels?: {
        true: string;
        false: string;
    };
    timezoneField?: string;
    fieldToText?: (value: Utils.UNSAFE_ANY) => string;
    // Pins the column to the right edge (sticky) so it stays visible while the
    // table scrolls horizontally. Composes with any setCellProps the column sets.
    pinRight?: boolean;
    options?: Omit<MUIDataTableColumnOptions, 'sortThirdClickReset' | 'viewColumns'>;
};
type ParsedColumn = MUIDataTableColumn;

export type PaginationOptions = {
    page: number;
    setPage: (page: number) => void;
    pageSize: number;
    setPageSize: (pageSize: number) => void;
    pageSizeOptions: number[];
    numRows: number;
};

const parseRow = <T,>({ row, columns }: { row: T; columns: RawColumn<T>[] }) => {
    const parsedRow: Record<string, string> = {};

    columns.forEach((column) => {
        if (!column.field) {
            return;
        }
        let value = _.get(row, column.field) as string;
        if (column.boolean) {
            value = value
                ? column.booleanLabels?.true ?? 'True'
                : column.booleanLabels?.false ?? 'False';
        } else if (column.fieldToText) {
            value = column.fieldToText(value);
        }
        if (isFieldEmpty(value)) {
            value = AXLE_TABLE_NONE_TEXT;
        }
        parsedRow[column.field] = value;
    });

    return parsedRow;
};

const parseFilterOptions = <T,>(data: T[], field: undefined | string): string[] => {
    if (!field) {
        return [];
    }

    const values: string[] = data.map((row) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const value = _.get(row, field);
        return typeof value === 'string' ? value : '';
    });

    // NOTE: This is handling the case where there are multiple values in a single table
    // cell, as we join the elements into a string before populating the table. We
    // currently don't stop users from using commas in the names of things, so this could
    // cause a bug in the future where values with a comma get split out in the filtering.
    const splitValues = values.flatMap((value) => value.split(', '));
    return removeUndefinedFromArray(uniqueByEquality(splitValues));
};

const isObject = (item: unknown): item is Record<string, unknown> =>
    typeof item === 'object' && item !== null;

const isKeyofObject = <T extends Record<string, unknown>>(item: T, key: unknown): key is keyof T =>
    typeof key === 'string' && key in item;

const getFieldValue = <T,>(data: T[], index: number, field?: string): T[keyof T] | null => {
    const item = data.at(index);
    if (isObject(item) && isKeyofObject(item, field)) {
        return item[field] as T[keyof T];
    }
    return null;
};

const getStringField = (field: unknown) => (typeof field === 'string' ? field : null);

const parseColumn = <T,>({
    column: {
        label,
        field,
        hidden = false,
        hiddenWithOptionToShow = false,
        mobile = false,
        rowToComponent,
        boolean,
        timezoneField,
        pinRight = false,
        options = {},
    },
    data,
    isMobileLayout,
    idx,
}: {
    column: RawColumn<T>;
    data: T[];
    isMobileLayout: boolean;
    idx: number;
}): ParsedColumn => {
    const column: ParsedColumn = {
        // Q: Why are we defining `ParsedColumn.name` like this? This seems kind of silly...
        //
        // A: The `name` field is how the <MUIDataTable/> identifies columns. Because I don't
        //  want to accidentally break anything, I'm just leaving the default to be `field`.
        //  However, for many of the columns passed into <AxleTable/>, the `field` is not
        //  defined. In those cases where we still want to identify the column, we use the
        //  `label` field from the passed in raw column.
        //
        //  Additionally, apparently <MUIDataTable/> will fallback to the `name` field if
        //  `label` is not defined. This becomes problematic when we have a column we
        //  want to display without a name (e.g. the edit column which adds a pencil icon
        //  to the row and doesn't have a name). In those cases, we set `name` to be the
        //  index times \xa0 (non-breaking space) which is a super hacky way to distinctly
        //  identify columns with blank names without having to display a name in the
        //  actual table. Note: we use `||` instead of `??` so we fallback when `field`
        //  or `label` are empty strings as well as when they are undefined.
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        name: field || label || '\xa0'.repeat(idx),
        label: label ?? '',
        options: {
            // Not accepted for overriding. (Omitted from the input type.)
            sortThirdClickReset: true,
            viewColumns: !!label,

            // Can be overridden,
            filterOptions: {
                names: parseFilterOptions(data, field),
            },

            // Overrides.
            ...options,
        },
    };

    // Convince TypeScript this won't be undefined.
    invariant(column.options !== undefined);

    if (hidden && hiddenWithOptionToShow) {
        throw new Error("Cannot have both 'hidden' and 'hiddenWithOptionToShow' set to true");
    }

    if (hidden) {
        column.options.display = 'excluded';
        column.options.filter = false;
    }

    if (hiddenWithOptionToShow) {
        column.options.display = false;
    }

    if (mobile) {
        column.options.filter = false;
        column.options.download = false;
        column.options.sort = false;
        column.options.searchable = false;
    }

    if (!isMobileLayout && mobile) {
        column.options.display = 'excluded';
    }

    if (isMobileLayout && !mobile) {
        column.options.display = 'excluded';
    }

    // Previously, we relied on the functionality that `display` being undefined
    //  caused the column to be displayed. We now want to be explicit about this.
    if (column.options.display === undefined) {
        column.options.display = true;
    }

    if (_.compact([rowToComponent, boolean, timezoneField]).length > 1) {
        throw new Error('Contradictory options for the column.');
    }

    if (!field && !rowToComponent) {
        throw new Error('field must be explicitly provided unless rowToComponent is provided.');
    }

    if (rowToComponent) {
        column.options.customBodyRenderLite = (dataIndex: number) => {
            const row = rowAtDataIndex(data, dataIndex);
            return row === undefined ? null : rowToComponent(row);
        };
    }

    // If the column is rendered by a custom component, but no field is supplied,
    //  then the column should not be filterable, sortable, or downloadable.
    if (rowToComponent && !field) {
        column.options.filter = false;
        column.options.download = false;
        column.options.sort = false;
        column.options.searchable = false;
    }

    if (boolean) {
        // TODO: We should add better typing/checking to make sure field is defined.
        invariant(field !== undefined);
        column.options.customBodyRenderLite = (dataIndex: number) => {
            return getFieldValue(data, dataIndex, field) ? <Check /> : <Close />;
        };
    }

    if (timezoneField) {
        // TODO: We should add better typing/checking to make sure field is defined.
        column.options.customBodyRenderLite = (dataIndex: number) => {
            let fieldValue = getFieldValue(data, dataIndex, field) as Date | null | Moment;
            if (fieldValue instanceof Date) {
                fieldValue = moment(fieldValue);
            }
            if (!(fieldValue === null || isMoment(fieldValue))) {
                throw new Error(
                    'Field must be null or a moment object to use timezoneField option.',
                );
            }
            return (
                <NullableFormattedDatetime
                    datetime={fieldValue}
                    timezone={getStringField(getFieldValue(data, dataIndex, timezoneField))}
                />
            );
        };

        // Disable filtering on datetime fields, as otherwise we might see
        //  too many options in the dropdown.
        column.options.filter = false;
    }

    if (pinRight) {
        const prevCellProps = column.options.setCellProps;
        const prevHeaderProps = column.options.setCellHeaderProps;
        column.options.setCellProps = (value, rowIndex, columnIndex) =>
            withPinnedSx(prevCellProps?.(value, rowIndex, columnIndex), pinnedRightCellSx);
        column.options.setCellHeaderProps = (columnMeta) =>
            withPinnedSx(prevHeaderProps?.(columnMeta), pinnedRightHeaderSx);
    }

    return column;
};

export type ServerSideSort = [sort: SortState | null, onSort: (state: SortState | null) => void];

export type AxleDataTableProps<Row> = {
    // Main options.
    title?: string | Component;
    data: Row[];
    components?: MUIDataTableProps['components'];
    onRowClick?: (row: Row) => void;
    rowToLink?: (row: Row) => string | undefined;
    columns: RawColumn<Row>[];
    // Loading state.
    loading?: boolean;
    // Mobile layout options.
    isMobileLayout?: boolean;
    // Styling options.
    setRowStyle?: (row: Row) => Style;
    elevation?: number;
    compact?: boolean;
    // Toolbar options.
    download?: boolean;
    filter?: boolean;
    search?: boolean;
    toolbar?: boolean;
    // Search options.
    searchState?: {
        searchTerm: string;
        setSearchTerm: (searchTerm: string) => void;
    };
    // Server-side sort options.
    serverSideSort?: ServerSideSort;
    noRecordsText?: string;
    // Pagination options.
    dynamicPaging?: PaginationOptions;
    // Selection options.
    disableSelection?: boolean;
    selectableRows?: 'none' | 'single' | 'multiple';
    rowToSelectionIdentifier?: (row: Row) => string;
    isRowSelectable?: (row: Row) => boolean;
    selectedRowValues?: string[];
    setSelectedRowValues?: (selectedRowValues: string[]) => void;
    // Actions Menu Options
    //  You can include a custom toolbar above the table that includes actions.
    //  NOTE: Requires `selectableRows` to be set to 'multiple'.
    includeCustomToolbar?: boolean;
    // Extra options.
    // We omit some props, are they required by MUI datatables, but already set above,
    //  but TypeScript doesn't know that.
    extraOptions?: MUIDataTableOptions;
    className?: string;
    // Floats left/right scroll arrows over the ends of the header row, each
    // shown only while there is distance to scroll that way, so wide tables are
    // navigable without scrolling to the bottom to reach a scrollbar. Pair with
    // a `pinRight` column to keep an actions column in view.
    horizontalScrollButtons?: boolean;
    // Tightens cell padding so more columns fit before the table overflows.
    condensedPadding?: boolean;
    // Pins the header row to the top of the page as it scrolls, keeping column
    // controls (e.g. an "Apply" button) in view. Driven by a JS transform since
    // the table's overflow:auto wrapper traps position:sticky.
    stickyHeader?: boolean;
};
export type ExtraAxleDataTableProps<Row> = Omit<AxleDataTableProps<Row>, 'data' | 'columns'>;

const DEFAULT_PAGE_SIZE = 50;
export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export const AxleTable = genericMemo(
    <Row,>({
        // Main options.
        title,
        className,
        data,
        components = {},
        // Column options.
        columns,
        loading = false,
        isMobileLayout = false,
        // Row click options.
        onRowClick,
        rowToLink,
        // Styling options.
        setRowStyle = () => ({}),
        elevation = 1,
        compact = false,
        // Toolbar options.
        download = false,
        filter = true,
        search = true,
        toolbar = true,
        // Search options.
        searchState,
        serverSideSort,
        noRecordsText,
        // Pagination options.
        dynamicPaging,
        // Selection options.
        disableSelection,
        selectableRows = 'none',
        rowToSelectionIdentifier, // Convert row to identifier for selection.
        isRowSelectable, // Disable selection on specific rows.
        selectedRowValues, // List of values of identifying field for selected rows.
        setSelectedRowValues, // Update the above value.
        // Actions Menu Options
        includeCustomToolbar = false,
        // Extra options for MUI datatables.
        extraOptions = {},
        horizontalScrollButtons = false,
        condensedPadding = false,
        stickyHeader = false,
    }: AxleDataTableProps<Row>) => {
        //////////////////////////////////////////////////////////////////////////////
        // Parsing data and columns.
        //////////////////////////////////////////////////////////////////////////////

        // Scroll-tracking table chrome (pinned header + horizontal scroll
        // arrows), off unless a caller opts in. The hook owns its DOM plumbing
        // and hands back the container ref to attach plus the arrow elements.
        const hasPinnedColumn = columns.some((column) => column.pinRight);
        const { containerRef, scrollButtons } = useStickyTableChrome({
            stickyHeader,
            horizontalScrollButtons,
            pinnedColumn: hasPinnedColumn,
            scrollContainer: MUI_DATATABLES_SCROLL_CONTAINER,
        });

        // For persisting selected columns when some state changes (e.g. selecting a row).
        //  If we do not track this manually, then when <MUIDataTable/> re-renders, it will
        //  only display the columns that are selected by default. Fortunately, most
        //  callers of <AxleTable/> do not need to worry about this, as they don't have
        //  selectable rows and/or all of their columns are selected by default.
        //
        //  The reason why this is conditionally set is because <MUIDataTable/> sucks
        //  and when a column is selected/deselected, we update the column state, causing
        //  a re-render, which immediately closes the column selection dialog.
        //  This means we can only ever select one column at a time :(
        //  Thus, we should only be setting this to true when we really need it.
        const persistColumns = selectableRows !== 'none';

        // TODO: Support a "rowToField" option, that pre-parses the data, and is always required, though allowed to be null.

        const dataParsed = useMemo(
            () => data.map((row) => parseRow({ row, columns })),
            [data, columns],
        );

        //////////////////////////////////////////////////////////////////////////////
        // Column selection options.
        //////////////////////////////////////////////////////////////////////////////

        const [sortField] = serverSideSort ?? [
            null,
            () => {
                // Do Nothing
            },
        ];

        const clientSort = useTableSort(
            extraOptions.sortOrder
                ? {
                      sortedByField: extraOptions.sortOrder.name,
                      direction: extraOptions.sortOrder.direction,
                  }
                : { sortedByField: '', direction: 'none' },
        );
        const ownsClientSort = !serverSideSort && extraOptions.sortOrder !== undefined;

        // Disable sort controls when only one row in table.
        const disableSort =
            data.length < 2 &&
            (!dynamicPaging || dynamicPaging.numRows / dynamicPaging.pageSize <= 1);

        // noinspection PointlessBooleanExpressionJS
        const columnsParsed = useMemo(() => {
            return columns.map((column, idx) => {
                const columnWithSort = disableSort
                    ? { ...column, options: { ...column.options, sort: false } }
                    : column;
                return parseColumn({
                    column: columnWithSort,
                    data,
                    isMobileLayout,
                    idx,
                });
            });
        }, [columns, data, isMobileLayout, disableSort]);

        const [columnsManuallySelected, setColumnsManuallySelected] = useState<
            Record<string, boolean>
        >(() => {
            const initialState: Record<string, boolean> = {};
            columnsParsed.forEach((column) => {
                invariant(column.options !== undefined);
                // `MuiDataTable` does some weird stuff under the hood when
                //  displaying columns. The switch statement below is to account
                //  for that. If modifying any of this code, please check with Drew
                switch (column.options.display) {
                    case true:
                    case undefined:
                    case 'excluded':
                        initialState[column.name] = true;
                        break;
                    case false:
                        initialState[column.name] = false;
                        break;
                    default:
                        throw new Error('Invalid column display option');
                }
            });
            return initialState;
        });
        const columnsParsedAndSelected = useMemo(() => {
            const parseAndDisplayColumn = (parsedColumn: ParsedColumn) => {
                invariant(parsedColumn.options !== undefined);
                if (parsedColumn.options.display === 'excluded') {
                    // no-op
                    parsedColumn.options.display = 'excluded';
                } else {
                    parsedColumn.options.display = columnsManuallySelected[parsedColumn.name];
                }
                return parsedColumn;
            };
            return columnsParsed.map(parseAndDisplayColumn);
        }, [columnsParsed, columnsManuallySelected]);

        const columnsVisibilityKey = useMemo(
            () =>
                JSON.stringify(
                    columnsParsedAndSelected.map((column) => ({
                        name: column.name,
                        display: column.options?.display,
                    })),
                ),
            [columnsParsedAndSelected],
        );

        const onViewColumnsChange = (changedColumn: string, action: string) => {
            if (!persistColumns) {
                return;
            }
            setColumnsManuallySelected((prev) => ({
                ...prev,
                [changedColumn]: action === 'add',
            }));
        };

        //////////////////////////////////////////////////////////////////////////////
        // Row clicking options.
        //////////////////////////////////////////////////////////////////////////////

        const openLinkWithNewTabSupport = useOpenLinkWithNewTabSupport();

        const onRowClickFinal = useMemo(
            () =>
                onRowClick || rowToLink
                    ? (((_: unknown, { dataIndex }: { dataIndex: number }, event: MouseEvent) => {
                          const row = rowAtDataIndex(data, dataIndex);
                          if (row === undefined) {
                              return;
                          }
                          if (onRowClick) {
                              onRowClick(row);
                              return;
                          }
                          if (rowToLink) {
                              openLinkWithNewTabSupport(rowToLink(row), event);
                          }
                      }) as unknown as MUIDataTableOptions['onRowClick'])
                    : // We have to do this cast, as the types for MUI datatables don't show the third event param being passed in, but it is in fact passed in.
                      undefined,
            [data, onRowClick, openLinkWithNewTabSupport, rowToLink],
        );

        //////////////////////////////////////////////////////////////////////////////
        // Pagination options.
        //////////////////////////////////////////////////////////////////////////////

        const [managedPageSize, setManagedPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
        const pageSize = useMemo(() => {
            if (dynamicPaging) {
                return dynamicPaging.pageSize;
            } else {
                return managedPageSize;
            }
        }, [dynamicPaging, managedPageSize]);

        const customFooter = useMemo(() => {
            if (dynamicPaging === undefined) {
                return undefined;
            }
            invariant(dynamicPaging.pageSizeOptions.includes(dynamicPaging.pageSize));
            const CustomFooter: FC = () => (
                <TableFooter>
                    <TableRow>
                        <TableCell
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                padding: '0px 24px 0px 24px',
                            }}
                            colSpan={1000}
                        >
                            <MuiTablePagination
                                component='div'
                                page={dynamicPaging.page}
                                onPageChange={(_event, newPage) => dynamicPaging.setPage(newPage)}
                                rowsPerPage={dynamicPaging.pageSize}
                                onRowsPerPageChange={(event) => {
                                    const newPageSize = parseInt(event.target.value);
                                    dynamicPaging.setPageSize(newPageSize);
                                }}
                                rowsPerPageOptions={dynamicPaging.pageSizeOptions}
                                count={dynamicPaging.numRows}
                                labelDisplayedRows={({ from, to, count }) =>
                                    `${from}–${to} of ${count}`
                                }
                            />
                        </TableCell>
                    </TableRow>
                </TableFooter>
            );
            return CustomFooter;
        }, [dynamicPaging]);

        //////////////////////////////////////////////////////////////////////////////
        // Selection options.
        //////////////////////////////////////////////////////////////////////////////

        const [selectedRowIndices, setSelectedRowIndices] = useState<number[]>([]);

        // Validate the inputs so everything works as expected
        if (selectableRows === 'none') {
            invariant(selectedRowValues === undefined, 'selectedRowValues must be undefined');
            invariant(
                rowToSelectionIdentifier === undefined,
                'rowToSelectionIdentifier must be undefined',
            );
            invariant(setSelectedRowValues === undefined, 'setSelectedRowValues must be undefined');
        } else {
            invariant(selectedRowValues !== undefined, 'selectedRowValues must be defined');
            invariant(
                rowToSelectionIdentifier !== undefined,
                'rowToSelectionIdentifier must be defined',
            );
            invariant(setSelectedRowValues !== undefined, 'setSelectedRowValues must be defined');
        }

        // Sometimes, the data just changes, like when you create a new object.
        //  In that case, we need to update the selected row indices to match the
        //  originally selected row ids.
        useEffect(() => {
            if (selectableRows === 'none') {
                return;
            }
            invariant(rowToSelectionIdentifier !== undefined);
            invariant(selectedRowValues !== undefined);
            setSelectedRowIndices(
                removeUndefinedFromArray(
                    data.map((obj, index) =>
                        selectedRowValues.includes(rowToSelectionIdentifier(obj))
                            ? index
                            : undefined,
                    ),
                ),
            );
        }, [data, rowToSelectionIdentifier, selectableRows, selectedRowValues]);

        const onRowSelectionChange = useMemo(
            () =>
                setSelectedRowValues === undefined
                    ? undefined
                    : // We have to type the third parameter as `unknown` to be compatible
                      //  with the typing of this function in MUI datatables.
                      (_: unknown, _2: unknown, selectedRowIndicesUntyped: unknown): void => {
                          invariant(rowToSelectionIdentifier !== undefined);

                          const selectedRowIndices = selectedRowIndicesUntyped as number[];
                          const selectedRowValues = selectedRowIndices.map(
                              (selectedRowIndex: number) =>
                                  rowToSelectionIdentifier(data[selectedRowIndex]),
                          );

                          setSelectedRowIndices(selectedRowIndices);
                          setSelectedRowValues(selectedRowValues);
                      },
            [data, rowToSelectionIdentifier, setSelectedRowValues],
        );

        //////////////////////////////////////////////////////////////////////////////
        // Actual table.
        //////////////////////////////////////////////////////////////////////////////

        // MUI DataTables derives the header checkbox state from every displayed row,
        // so a table with non-selectable rows sticks in the indeterminate state once
        // all selectable rows are checked. Recompute it against the selectable subset.
        const selectableRowCount = useMemo(
            () => (isRowSelectable ? data.filter(isRowSelectable).length : data.length),
            [data, isRowSelectable],
        );
        const allSelectableRowsChecked =
            selectableRowCount > 0 && selectedRowIndices.length >= selectableRowCount;
        const anyRowChecked = selectedRowIndices.length > 0;
        const headerCheckboxState = useMemo(
            () =>
                isRowSelectable
                    ? {
                          checked: allSelectableRowsChecked,
                          indeterminate: anyRowChecked && !allSelectableRowsChecked,
                      }
                    : undefined,
            [isRowSelectable, allSelectableRowsChecked, anyRowChecked],
        );

        // When selection is disabled (e.g. the sibling selection column is active),
        // MUI DataTables hardcodes the header select-cell to selectable, so its
        // checkbox always renders enabled. Override the header checkbox to render
        // greyed-out-but-visible — matching the disabled body checkboxes — instead of
        // hiding it. Identified via MUI's `data-description="row-select-header"`.
        const tableComponents = useMemo(() => {
            const base: MUIDataTableProps['components'] = isRowSelectable
                ? { ...components, TableToolbarSelect: () => null }
                : components;
            if (!disableSelection && !isRowSelectable) {
                return base;
            }
            const BaseCheckbox = (components.Checkbox ?? Checkbox) as ElementType;
            const HeaderCheckbox: FC<MUIDataTableCheckboxProps> = (checkboxProps) => {
                const isHeaderCheckbox =
                    (checkboxProps as { 'data-description'?: string })['data-description'] ===
                    'row-select-header';
                if (!isHeaderCheckbox) {
                    return <BaseCheckbox {...checkboxProps} />;
                }
                return (
                    <BaseCheckbox
                        {...checkboxProps}
                        {...headerCheckboxState}
                        disabled={disableSelection ? true : checkboxProps.disabled}
                    />
                );
            };
            return { ...base, Checkbox: HeaderCheckbox };
        }, [components, disableSelection, isRowSelectable, headerCheckboxState]);

        return (
            <>
                <Box
                    ref={containerRef}
                    className={className}
                    sx={{
                        position: 'relative',
                        ...(loading && {
                            height: '300px',
                            overflow: 'hidden',
                        }),
                        // Sticky header: the JS transform (useStickyTableChrome) pins the
                        // thead to the page scroller. It must be positioned for z-index to
                        // apply and opaque so scrolled rows don't show through.
                        ...(stickyHeader && {
                            '& thead': {
                                position: 'relative',
                                // Checkboxes sit at z-index 100.
                                zIndex: 101,
                                backgroundColor: 'background.paper',
                                opacity: 1,
                                willChange: 'transform',
                            },
                        }),
                        // Make it obvious if the row is clickable.
                        //  Without this, the user has no way of knowing if the row is clickable!
                        '& tbody tr:hover': {
                            cursor: onRowClickFinal ? 'pointer' : undefined,
                        },
                        // Make the table compact.
                        '& .MuiTableCell-body': {
                            padding: compact ? '5px' : undefined,
                        },
                        // Condensed padding fits more columns before the table
                        // overflows. Header and body cells take the same value so
                        // the columns stay evenly spaced, and the horizontal half
                        // keeps a gutter between neighbouring columns.
                        ...(condensedPadding && {
                            '& .MuiTableCell-root': { padding: '4px 8px' },
                        }),
                        '& .MuiTableCell-footer': {
                            border: 'none',
                        },
                    }}
                >
                    {loading && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                backgroundColor: 'white',
                                zIndex: 1000,
                                minHeight: '200px',
                            }}
                        >
                            <CircularProgress sx={{ color: '#aeaeae' }} />
                        </Box>
                    )}
                    <MUIDataTable
                        // Want to make the table reload if the column options have changed,
                        //  otherwise it is not taken into account.
                        // React needs `key` to be a number or string, so we use JSON.stringify(...).
                        // `disableSelection` is included because MUI DataTables caches row
                        //  selectability and won't re-evaluate `isRowSelectable` when the prop
                        //  flips back to enabled without a remount.
                        key={`${columnsVisibilityKey}-${disableSelection ? 'locked' : 'unlocked'}`}
                        title={title}
                        data={dataParsed as object[]}
                        columns={columnsParsedAndSelected}
                        components={tableComponents}
                        options={{
                            // Row click options.
                            onRowClick: onRowClickFinal,
                            // Pagination options.
                            // We need to provide this even if we're using `dynamicPaging`, because it determines
                            //  how many rows MUI Datatables shows.
                            rowsPerPage: pageSize,
                            // If `dynamicPaging` is provided, this callback doesn't matter, as a custom footer will be rendered, and this function will never be called.
                            onChangeRowsPerPage: setManagedPageSize,
                            // Passing in a single-item list here hides the dropdown entirely.
                            // If `dynamicPaging` is provided, this value doesn't matter, as a custom footer will be rendered instead of the default one.
                            rowsPerPageOptions: DEFAULT_PAGE_SIZE_OPTIONS,
                            // If `dynamicPaging` is provided, this renders a custom footer in place of the default one.
                            customFooter: customFooter,
                            // Toolbar.
                            print: false, // Disable the "print" feature.
                            download: download,
                            filter: filter,
                            search: search,
                            // Styling.
                            elevation: elevation,
                            setRowProps: (_, dataIndex: number) => {
                                const row = rowAtDataIndex(data, dataIndex);
                                return row === undefined ? {} : { style: setRowStyle(row) };
                            },
                            // Mobile layout options.
                            responsive: isMobileLayout ? 'standard' : 'vertical',
                            viewColumns: !isMobileLayout, // Don't show column visibility selector in mobile layout.
                            // Filtering options.
                            filterType: 'multiselect', // Default filter selector, but can be overridden per field.
                            // Search options.
                            ...(searchState && {
                                // The `searchAlwaysOpen` option seems to be buggy; if you hit Escape,
                                //  then the search bar disappears with no way to bring it back. Hence,
                                //  I am using the `searchOpen` option instead.
                                serverSide: true, // We are handling the search ourselves.
                                searchText: searchState.searchTerm,
                                searchOpen: true,
                                // Sometimes the searchTerm is null, so we have the `|| ''`.
                                onSearchChange: (searchTerm: string | null) =>
                                    searchState.setSearchTerm(searchTerm ?? ''),
                                // Note: We don't use debounceSearchRender here because debouncing
                                // is now handled at the parent component level via useDebouncedSearch hook.
                                // This prevents the search input from being cleared during data loading.
                            }),
                            ...(serverSideSort && {
                                serverSide: true,
                                sortOrder:
                                    sortField && sortField.direction !== 'none'
                                        ? {
                                              name: sortField.sortedByField,
                                              direction: sortField.direction,
                                          }
                                        : undefined,
                                onColumnSortChange: (column: string, direction: string) =>
                                    serverSideSort[1](
                                        direction
                                            ? {
                                                  sortedByField: column,
                                                  direction: direction as SortDirection,
                                              }
                                            : null,
                                    ),
                            }),
                            ...(noRecordsText && {
                                textLabels: {
                                    body: {
                                        noMatch: noRecordsText,
                                    },
                                },
                            }),
                            // Selection options.
                            // NOTE: avoid 'none' when isRowSelectable is set — MUI DataTables
                            // has a bug where selectToolbarPlacement:'none' forces deselect-all
                            // whenever any row is non-selectable, breaking "select all".
                            selectToolbarPlacement:
                                includeCustomToolbar || isRowSelectable ? 'above' : 'none',
                            ...(isRowSelectable && {
                                customToolbarSelect: () => null,
                            }),
                            selectableRows: selectableRows,
                            // MUIDataTables pins the select column by default, which on wide
                            // (horizontally scrolling) tables lets other columns bleed through
                            // it. Keep it unpinned — only the actions column is worth pinning.
                            fixedSelectColumn: false,
                            // When the other selection column is active, make every row
                            // non-selectable. The header checkbox still renders so it can
                            // be shown greyed-out-but-visible (see `tableComponents`),
                            // rather than disappearing. Otherwise show the select-all
                            // header checkbox only when there is at least one row available
                            // to check, hiding it entirely when there's nothing to select.
                            selectableRowsHeader: disableSelection
                                ? true
                                : isRowSelectable
                                ? data.some(isRowSelectable)
                                : data.length > 0,
                            rowsSelected: selectedRowIndices,
                            onRowSelectionChange: onRowSelectionChange,
                            ...(disableSelection
                                ? { isRowSelectable: () => false }
                                : isRowSelectable
                                ? {
                                      isRowSelectable: (dataIndex: number) => {
                                          const row = rowAtDataIndex(data, dataIndex);
                                          return row !== undefined && isRowSelectable(row);
                                      },
                                  }
                                : {}),
                            onViewColumnsChange: onViewColumnsChange,
                            // Removing the toolbar entirely.
                            //  (This needs to come after all the previous options, so it can
                            //  override them.)
                            ...(!toolbar && {
                                print: false,
                                search: false,
                                download: false,
                                filter: false,
                                viewColumns: false,
                            }),
                            // Extra options.
                            ...extraOptions,
                            ...(ownsClientSort && {
                                sortOrder: toMuiSortOrder(clientSort.sort),
                                onColumnSortChange: (column: string, direction: string) =>
                                    clientSort.sortBy(
                                        column,
                                        (direction || 'none') as SortDirection,
                                    ),
                            }),
                        }}
                    />
                    {scrollButtons}
                </Box>
            </>
        );
    },
);
AxleTable.displayName = 'AxleTable';
